import { useFpCampaignCartCalculate } from '@cart-services/hooks/useFpCampaignCartCalculate'
import { useFpCampaignCartService } from '@cart-services/hooks/useFpCampaignCartService'
import { useFpCampaignCoupons } from '@cart-services/hooks/useFpCampaignCoupons'
import { useFpCampaignProductsInfo } from '@cart-services/hooks/useFpCampaignProductsInfo'
import { getDisplayAttributes } from '@cart-services/parser/parseAttributes'
import { parserFastpassOrderItemBySkuMap } from '@cart-services/parser/parserFastpassOrderItemBySkuMap'
import { type CartItem } from '@cart-services/types'
import { type ProjectCode } from '@constants'
import { sendFastpassAddToCartEvent, useFastpassCampaignGoogleAnalyticEvent } from '@dni-analysis'
import { generateItemVariant } from '@dni-analysis/events/utils'
import { useDialog } from '@dni-hooks/useDialog'
import { CouponType } from '@hooks-api/types/dataModel/coupon'
import { type DeliveryClass, DELIVERY_CLASS } from '@hooks-api/types/dataModel/shipping'
import { HStack } from '@pets-ui/HStack/HStack'
import { TrashOutlined } from '@pets-ui/icons'
import { logoMap } from '@pets-ui/Logos/index'
import { Paper } from '@pets-ui/Paper/Paper'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { parseToNumber } from '@utils'
import { useMemo } from 'react'
import { CartList } from '../CartList/CartList'
import { CartListItem } from '../CartListItem/CartListItem'
import { useContextCampaignCart } from '../CartProvider/CampaignCartProvider'
import { DIALOG_SELECT_PRODUCT_VARIATION, type DialogSelectProductVariationProps } from '../DialogSelectProductVariation/DialogSelectProductVariation'

interface CartListItemsGroupByProjectProps {
  projectCode: ProjectCode
  items: CartItem[]
}

const DeliveryClassLabelMap: Record<'normal' | 'frozen', string> = {
  [DELIVERY_CLASS.NORMAL]: '常溫商品',
  [DELIVERY_CLASS.FROZEN]: '冷凍商品',
}

const _baseStyles = stylex.create({
  root: {
    borderRadius: '8px',
    boxShadow: 'none',
  },
  header: {
    padding: '8px 12px',
    borderRadius: '8px 8px 0 0',
    alignItems: 'center',
    backgroundColor: '#E8E8E8',
    display: 'flex',
    justifyContent: 'space-between',
  },
})

const CartListItemsGroupByProject = ({ projectCode, items }: CartListItemsGroupByProjectProps) => {
  const { currency, campaignId, countryCode, campaignCode } = useContextCampaignCart()
  const { upsertCartItem, deleteCartItem } = useFpCampaignCartService(campaignId)
  const { destructuredData: campaignCouponCalculateResult } = useFpCampaignCartCalculate(campaignId, campaignCode)
  const { getMergedCouponCriterions } = useFpCampaignCoupons(campaignId, countryCode, campaignCode)
  const { openDialog } = useDialog<DialogSelectProductVariationProps>(DIALOG_SELECT_PRODUCT_VARIATION)
  const { isProductsLoading, productMap } = useFpCampaignProductsInfo({ campaignId, campaignCode, countryCode })
  const { sendRemoveFromCartEvent, sendRemoveProductEvent } = useFastpassCampaignGoogleAnalyticEvent({ projectCode, countryCode, campaignId })

  const sortedItemsByDeliveryClass = items.reduce<Partial<Record<DeliveryClass, CartItem[]>>>((acc, item) => {
    const product = productMap?.[item.cartItemId]
    const { delivery_class } = product ?? {}
    if (!delivery_class) return acc
    if (!acc[delivery_class]) {
      acc[delivery_class] = []
    }
    acc[delivery_class].push(item)
    return acc
  }, {})

  const handleQuantityChange = (cartItemId: string | number) => (value?: number) => {
    if (value === undefined || value === 0) return
    const currentItem = items.find(item => item.cartItemId === cartItemId)
    const isAddedToCart = (currentItem?.quantity || 0) < value
    if (isAddedToCart) {
      sendFastpassAddToCartEvent({
        campaign_code: campaignCode,
        currency,
        value: Number(currentItem?.sale_price),
        items: [{
          item_name: currentItem?.name || '',
          item_id: String(currentItem?.cartItemId),
          item_brand: projectCode,
          item_variant: generateItemVariant({
            productName: currentItem?.name || '',
            attributes: currentItem?.attributes,
          }),
          quantity: value,
          price: Number(currentItem?.sale_price),
          currency,
        }],
      })
    }

    upsertCartItem({
      cartItemId,
      quantity: value,
    })
  }

  /**
   * 後續要讓使用者可以自行上傳 brand logo
   *
   * 也需要請後端開欄位
   *
   * @deprecated
   *
   */
  const Logo = logoMap[projectCode.toLowerCase() as keyof typeof logoMap]

  const handleDelete = (cartItemId: string | number) => () => {
    const item = items.find(item => item.cartItemId === cartItemId)
    const product = productMap?.[cartItemId]

    if (item && product) {
      sendRemoveProductEvent({
        productSku: item.sku,
        productRealSku: product.real_sku || '',
        variationId: Number(cartItemId),
        productName: item.name,
        productPrice: Number(item.sale_price) || 0,
      })
    }

    deleteCartItem({ cartItemId })
  }

  const handleDeleteAllByProjectCode = () => {
    items.forEach((item) => {
      deleteCartItem({ cartItemId: item.cartItemId })
    })
    if (currency) {
      sendRemoveFromCartEvent({
        currency,
        value: campaignCouponCalculateResult?.total || 0,
        items: items.map(item => ({
          item_id: String(item.cartItemId),
          quantity: item.quantity,
          item_brand: projectCode,
          item_name: item.name,
          price: Number(item.sale_price) || 0,
          item_variant: generateItemVariant({
            productName: item.name,
            attributes: item.attributes,
          }),
        })),
      })
    }
  }

  const handleActionClick = (parentProductId: number, productProjectCode: ProjectCode, currentCartItem: CartItem) => () => {
    openDialog({
      currency,
      parentProductId,
      productProjectCode,
      currentCartItem,
    })
  }

  const orderItemBySkuMap = useMemo(() => parserFastpassOrderItemBySkuMap(campaignCouponCalculateResult?.order_items), [campaignCouponCalculateResult])
  return (
    <>
      {Object.entries(sortedItemsByDeliveryClass).map(([deliveryClass, items]) => {
        return (
          <Paper rootStyle={_baseStyles.root} key={deliveryClass}>
            <div {...stylex.props(_baseStyles.header)}>
              <HStack gap={2} alignItems="center">
                <Logo fontSize={20} />
                <Text variant="subtitle" color="grey900">{DeliveryClassLabelMap[deliveryClass as keyof typeof DeliveryClassLabelMap]}</Text>
              </HStack>
              <TrashOutlined cursor="pointer" fontSize={20} color={colors.grey500} onClick={handleDeleteAllByProjectCode} />
            </div>
            {items?.map((item) => {
              const orderItemFromCalculate = orderItemBySkuMap[item.sku]
              const limitedQuantity = item.limited_quantity
              const product = productMap?.[item.cartItemId]
              // NOTE: 必須抓最新的庫存，按下前往結帳後如果有商品不足的彈窗將會更新 productMap
              const stock = Math.max(parseToNumber(product?.stock), 0)
              const couponCriterions = getMergedCouponCriterions(
                orderItemFromCalculate?.applied_coupon_ids ?? [],
                [CouponType.SPECIFIC_PRODUCT, CouponType.WAREHOUSE_PRODUCT],
              )

              if (!product) return null

              return (
                <CartListItem
                  key={item.cartItemId}
                  quantity={item.quantity}
                  maxQuantity={limitedQuantity ? Math.min(limitedQuantity, stock) : stock}
                  salePrice={orderItemFromCalculate?.sale_price ? Number(orderItemFromCalculate?.sale_price) : Number(product.sale_price)}
                  originalPrice={product.original_price ? Number(product.original_price) : Number(product.sale_price)}
                  stock={stock}
                  productImgSrc={product.thumbnail ?? ''}
                  productTitle={product.title}
                  currency={currency}
                  couponCriterions={couponCriterions}
                  onQuantityChange={handleQuantityChange(item.cartItemId)}
                  onDelete={handleDelete(item.cartItemId)}
                  onSelectVariantClick={handleActionClick(product.parent_product_id, product.project_code, item)}
                  attributes={getDisplayAttributes(product)}
                  isAddon={item.is_addon}
                  isLoading={isProductsLoading}
                />
              )
            })}
          </Paper>
        )
      })}
    </>
  )
}

export const CartListItems = () => {
  const { campaignId } = useContextCampaignCart()
  const { items: cartItems } = useFpCampaignCartService(campaignId)

  const sortedCartItemsByProjectCode = cartItems.reduce<Partial<Record<ProjectCode, CartItem[]>>>((acc, item) => {
    const { project_code } = item
    if (!acc[project_code]) {
      acc[project_code] = []
    }
    acc[project_code].push(item)
    return acc
  }, {})

  const displayCartItems
    = Object.entries(sortedCartItemsByProjectCode)
      .filter(([, items]) => items.length)

  return (
    <>
      <CartList>
        {displayCartItems.map(([projectCode, items]) => (
          <CartListItemsGroupByProject key={projectCode} projectCode={projectCode as ProjectCode} items={items} />
        ))}
      </CartList>
    </>
  )
}
